import { createPortal } from 'react-dom';
import { Fragment, type ReactNode } from 'react';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Tooltip } from '@flintmint/web-shared/components/tooltip';
import { Divider } from '@flintmint/web-shared/components/divider';
import { Alert, type AlertProps } from '@flintmint/web-shared/components/alert';
import { ActionBar, type ActionBarProps } from '@flintmint/web-shared/components/action-bar';

import { useItemActions } from '@/hooks/use-item-actions';

import type { DocumentWithType } from '@/constants/types';
import { getSelectedItemType } from '@/constants/item-types';
import type { MenuItemProps } from '@flintmint/web-shared/components/menu-item';

import styles from './mini-action-bar.module.scss';

export const MiniActionBar = (props: MiniActionBarProps) => {
  const { children, onRemoveFromTag, onOpenItems, onManageTags, selections = [], withPortal, onClear, ...rest } = props;

  const { configureDestructiveOptions } = useItemActions();

  const numSelections = selections.length;
  const hasSelections = numSelections > 0;

  const destructiveOptions = configureDestructiveOptions(selections, { onDone: onClear });

  const firstItemType = getSelectedItemType(selections[0]?.type ?? '');

  const renderDestructiveAction = (option: MenuItemProps, disabled: boolean) => {
    const name = option.name ?? 'Delete';
    const icon = option.icon ?? 'delete-01';

    if (disabled) {
      return (
        <Tooltip key={name} content={name}>
          <button aria-label={name} disabled>
            <Icon type={icon} className={option.danger ? styles.delete : undefined} />
          </button>
        </Tooltip>
      );
    }

    return (
      <Alert
        key={name}
        type="danger"
        popoverProps={{
          placement: 'top',
          offset: [0, 16],
        }}
        trigger={
          <div className={option.danger ? 'danger' : undefined}>
            <Tooltip content={name}>
              <button aria-label={name}>
                <Icon type={icon} className={option.danger ? styles.delete : undefined} />
              </button>
            </Tooltip>
          </div>
        }
        {...(option.deleteData as AlertProps)}
      />
    );
  };

  const renderActionBar = (
    <ActionBar {...rest} clearable showBar onClear={onClear} appearance="dark">
      <div className={styles.miniActionBar}>
        <span>{numSelections} selected</span>
        <Divider size={24} />
        {!!onOpenItems && (
          <>
            <Tooltip content={numSelections > 1 ? `Open ${numSelections} items` : 'Open'}>
              <button disabled={!hasSelections} onClick={onOpenItems} aria-label="Open Selected Items">
                <Icon type="link-square-02" />
              </button>
            </Tooltip>
            <Divider size={24} />
          </>
        )}
        {!!onManageTags && (
          <>
            <Tooltip content="Manage Tags">
              <button disabled onClick={onManageTags} aria-label="Manage Tags">
                <Icon type="tags" />
              </button>
            </Tooltip>
            <Divider size={24} />
          </>
        )}
        {!!onRemoveFromTag && (
          <>
            {hasSelections ? (
              <Alert
                type="danger"
                icon="unlink-04"
                title={<>Remove From Tag</>}
                message={`Are you sure you want to remove this ${
                  selections?.length > 1 ? 'items' : firstItemType
                } from this tag? This action can’t be undone.`}
                primaryButton={{
                  children: 'Remove',
                  onClick: onRemoveFromTag,
                }}
                secondaryButton={{
                  children: 'Keep',
                }}
                trigger={
                  <div>
                    <Tooltip content="Remove From Tag">
                      <button aria-label="Remove From Tag">
                        <Icon type="unlink-04" className={styles.delete} />
                      </button>
                    </Tooltip>
                  </div>
                }
              />
            ) : (
              <Tooltip content="Remove From Tag">
                <button aria-label="Remove From Tag" disabled>
                  <Icon type="unlink-04" className={styles.delete} />
                </button>
              </Tooltip>
            )}
            <Divider size={24} />
          </>
        )}
        {!!children && (
          <>
            {children}
            <Divider size={24} />
          </>
        )}
        {destructiveOptions.map((option, index) => (
          <Fragment key={option.name ?? index}>
            {index > 0 && <Divider size={24} />}
            {renderDestructiveAction(option, !hasSelections)}
          </Fragment>
        ))}
      </div>
    </ActionBar>
  );

  return withPortal ? createPortal(renderActionBar, document.body) : renderActionBar;
};

export interface MiniActionBarProps extends ActionBarProps {
  onClear: () => void;
  withPortal?: boolean;
  children?: ReactNode;
  onOpenItems?: () => void;
  onManageTags?: () => void;
  onRemoveFromTag?: () => void;
  selections?: DocumentWithType[];
}
